import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { BarChartProps, Chart, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { SkeletonLoader } from '@frontend/design-system';

type CategoryChartProps = {
  categories: Record<string, number>;
  isLoading?: boolean;
  showChartLoader?: boolean;
  onBarClick?: BarChartProps['onClick'];
};

const BAR_APPEARANCE = {
  showXAxis: true,
  height: 150,
  barRadius: 4,
  barSize: 20,
  showGridLines: false,
  margin: { bottom: 20, top: 10 },
};

export const CategoryChart = memo(
  ({ categories = {}, isLoading = false, showChartLoader = false, onBarClick }: CategoryChartProps) => {
    const { t } = useTranslation('callIntelInsights');

    const { dataColors, chartLabelColors, chartOutlineColors, dataLabels } = useCallIntelDefaultsShallowStore(
      'dataColors',
      'chartLabelColors',
      'chartOutlineColors',
      'dataLabels'
    );

    const isEmptyValue = useMemo(() => Object.values(categories).every((v) => !v), [categories]);

    const groups = useMemo(
      () =>
        Object.entries(categories).map(([name, value]) => ({
          name,
          values: { [name]: value },
        })),
      [categories]
    );

    const colors = dataColors.categories ?? {};
    const labelColors = chartLabelColors.categories ?? {};
    const strokeColors = chartOutlineColors.categories ?? {};
    const labels = dataLabels.categories ?? {};

    if (isLoading && !showChartLoader) {
      return <SkeletonLoader height={184} />;
    }

    return (
      <Chart
        css={styles.containerStyles}
        colors={colors}
        labels={labels}
        strokeColors={strokeColors}
        isLoading={isLoading}
        emptyStateConfig={{ label: t('No data to display.'), style: { height: 127 } }}
      >
        <Chart.BarChart
          css={[isEmptyValue && styles.emptyChart]}
          data={{ groups }}
          formatValue={formatters.value}
          appearance={{
            ...BAR_APPEARANCE,
            tooltipClickHelperText: t('Click to view calls'),
            customTooltipData: (id) => {
              return [
                {
                  color: dataColors.categories?.[id] || '',
                  formattedValue: formatters.value(categories[id]),
                  id,
                  label: t('Calls'),
                },
              ];
            },
            customXAxisTick: ({ labels, groupName, ...rest }) => {
              const key = groupName as keyof typeof colors;
              return (
                <XAxisLabelValueTick
                  {...rest}
                  color={labelColors[key]}
                  label={labels?.[groupName] || groupName}
                  labelColor={theme.colors.neutral50}
                  labelFontSize={12}
                  lineGap={8}
                  value={formatters.value(categories[key])}
                  valueFontSize={14}
                />
              );
            },
          }}
          onClick={onBarClick}
        />
      </Chart>
    );
  }
);

CategoryChart.displayName = 'CategoryChart';

const styles = {
  containerStyles: css`
    margin-bottom: ${theme.spacing(0)};
    height: 184px;

    > div {
      border-radius: ${theme.borderRadius.small};
      padding: ${theme.spacing(2)};

      > div {
        padding-bottom: ${theme.spacing(0)} !important;
      }
    }
  `,
  emptyChart: css`
    > div {
      min-height: 127px !important;
      overflow-y: hidden;

      p {
        font-size: ${theme.fontSize(12)};
      }
    }
  `,
};
